import React, { useState } from "react";
import { Search, ChevronDown, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import RegistrationModal from "./RegistrationModal";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("registration");

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
    setServicesOpen(false);
  };

  const trademarkServices = [
    {
      name: "Trademark Search & Registration",
      path: "/services/trademark-search",
    },
    { name: "Trademark Monitoring", path: "/services/trademark-monitoring" },
    { name: "Trademark Clearance", path: "/services/trademark-clearance" },
    { name: "Office Actions & Amendments", path: "/services/office-actions" },
    { name: "Trademark Application", path: "/services/trademark-application" },
    { name: "Statement of Use", path: "/services/statement-of-use" },
    {
      name: "Change of Ownership/Transfer",
      path: "/services/trademark-transfer",
    },
    { name: "Portfolio Management", path: "/services/portfolio-management" },
  ];

  const copyrightServices = [
    {
      name: "Copyright Registration",
      path: "/services/copyright-registration",
    },
    { name: "Copyright Enforcement", path: "/services/copyright-enforcement" },
    { name: "Copyright Licensing", path: "/services/copyright-licensing" },
    { name: "Copyright Litigation", path: "/services/copyright-litigation" },
    { name: "Copyright Portfolio", path: "/services/copyright-portfolio" },
    { name: "DMCA Takedowns", path: "/services/dmca" },
  ];

  const patentServices = [
    { name: "Patent Searches", path: "/services/patent-searches" },
    {
      name: "Office Actions & Amendments",
      path: "/services/patent-office-actions",
    },
    { name: "Patent Applications", path: "/services/patent-applications" },
    { name: "Patent Enforcement", path: "/services/patent-enforcement" },
  ];

  const companyFormationServices = [
    { name: "LLC Formation", path: "/services/llc-formation" },
    { name: "C Corporation", path: "/services/c-corp-formation" },
    { name: "S Corporation", path: "/services/s-corp-formation" },
    { name: "Non-Profit (501c3)", path: "/services/nonprofit-formation" },
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/assets/img/brand/logo-dark.png"
              alt="Global Trademark Filer"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-900 hover:text-[#168356] transition-colors"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-900 hover:text-[#168356] transition-colors"
            >
              About Us
            </Link>
            <div
              className="relative group"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-[#168356] transition-colors h-16">
                Services
                <ChevronDown className="w-4 h-4" />
              </button>

              {servicesOpen && (
                <div className="fixed top-16 left-0 right-0 bg-white shadow-2xl border-t-2 border-[#168356] z-50">
                  <div className="max-w-7xl mx-auto px-8 py-10">
                    <div className="grid grid-cols-5 gap-8">
                      {/* Trademark Services */}
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#168356]">
                          Trademark Services
                        </h3>
                        <div className="space-y-0.5">
                          {trademarkServices.map((service, index) => (
                            <Link
                              key={index}
                              to={service.path}
                              className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all rounded-lg group"
                            >
                              <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center group-hover:from-[#168356]/20 group-hover:to-[#0a5938]/20 transition-all flex-shrink-0">
                                <svg
                                  className="w-4 h-4 text-[#168356]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <span className="text-xs leading-tight">
                                {service.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Copyright Services */}
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#168356]">
                          Copyright Services
                        </h3>
                        <div className="space-y-0.5">
                          {copyrightServices.map((service, index) => (
                            <Link
                              key={index}
                              to={service.path}
                              className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all rounded-lg group"
                            >
                              <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center group-hover:from-[#168356]/20 group-hover:to-[#0a5938]/20 transition-all flex-shrink-0">
                                <svg
                                  className="w-4 h-4 text-[#168356]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                  />
                                </svg>
                              </div>
                              <span className="text-xs leading-tight">
                                {service.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Patent Services */}
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#168356]">
                          Patent Services
                        </h3>
                        <div className="space-y-0.5">
                          {patentServices.map((service, index) => (
                            <Link
                              key={index}
                              to={service.path}
                              className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all rounded-lg group"
                            >
                              <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center group-hover:from-[#168356]/20 group-hover:to-[#0a5938]/20 transition-all flex-shrink-0">
                                <svg
                                  className="w-4 h-4 text-[#168356]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                  />
                                </svg>
                              </div>
                              <span className="text-xs leading-tight">
                                {service.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Company Formation Services */}
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#168356]">
                          Company Formation
                        </h3>
                        <div className="space-y-0.5">
                          {companyFormationServices.map((service, index) => (
                            <Link
                              key={index}
                              to={service.path}
                              className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all rounded-lg group"
                            >
                              <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center group-hover:from-[#168356]/20 group-hover:to-[#0a5938]/20 transition-all flex-shrink-0">
                                <svg
                                  className="w-4 h-4 text-[#168356]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                  />
                                </svg>
                              </div>
                              <span className="text-xs leading-tight">
                                {service.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Right Side CTA */}
                      <div className="bg-gradient-to-br from-[#168356]/5 to-[#0a5938]/5 rounded-2xl p-6 border-2 border-[#168356]/20">
                        <div className="flex flex-col items-center text-center h-full justify-between">
                          <div>
                            <div className="mb-4">
                              <svg
                                className="w-24 h-24 mx-auto text-[#168356]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                              </svg>
                            </div>
                            <h4 className="text-base font-bold text-gray-900 mb-2">
                              Ready to Protect Your Brand?
                            </h4>
                            <p className="text-xs text-gray-600 mb-4">
                              Start your trademark registration today with
                              expert guidance
                            </p>
                          </div>
                          <div className="w-full space-y-2">
                            <button
                              onClick={() => openModal("registration")}
                              className="w-full bg-[#d29b00] hover:bg-[#a87b00] text-white font-semibold py-3 px-6 rounded-xl transition-all hover:shadow-lg hover:scale-105 text-sm"
                            >
                              Start Registration →
                            </button>
                            <button
                              onClick={() => openModal("attorney")}
                              className="w-full border-2 border-[#d29b00] hover:bg-[#d29b00] text-[#d29b00] hover:text-white font-semibold py-3 px-6 rounded-xl transition-all hover:shadow-lg text-sm"
                            >
                              Discuss with Attorney
                            </button>
                            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
                              <svg
                                className="w-4 h-4 text-green-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>No hidden fees</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-[#168356] transition-colors h-16">
                Resources
                <ChevronDown className="w-4 h-4" />
              </button>

              {resourcesOpen && (
                <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  <Link
                    to="/resources/blogs"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-[#168356]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                        />
                      </svg>
                    </div>
                    <span>Blogs</span>
                  </Link>
                  <Link
                    to="/resources/news"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-[#168356]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    </div>
                    <span>News & Updates</span>
                  </Link>
                  <Link
                    to="/resources/publications"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:text-[#168356] hover:bg-[#168356]/5 transition-all"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-[#168356]/10 to-[#0a5938]/10 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-[#168356]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <span>Publications</span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="text-sm font-medium text-gray-900 hover:text-[#168356] transition-colors"
            >
              Contact
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-4">
            <Search className="w-5 h-5 cursor-pointer hover:text-[#168356] transition-colors text-gray-900" />
            <span className="text-sm font-medium text-gray-900">
              +1 (888) 669-0240
            </span>
            <span className="text-sm font-medium cursor-pointer hover:text-[#168356] transition-colors text-gray-900">
              Log In
            </span>
            <Button
              onClick={() => openModal("registration")}
              className="bg-[#d29b00] text-white hover:bg-[#a87b00] transition-all duration-300 border-2 border-[#d29b00]"
            >
              Register Trademark
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/"
              className="block text-sm font-medium text-gray-900 py-2"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block text-sm font-medium text-gray-900 py-2"
            >
              About Us
            </Link>

            <div className="py-2">
              <div className="text-sm font-bold text-gray-900 mb-2">
                Services
              </div>

              <div className="ml-4 space-y-2">
                <div className="text-xs font-bold text-[#168356] uppercase mt-2">
                  Trademark
                </div>
                {trademarkServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="block text-xs text-gray-700 py-1 pl-2"
                  >
                    {service.name}
                  </Link>
                ))}

                <div className="text-xs font-bold text-[#168356] uppercase mt-2">
                  Copyright
                </div>
                {copyrightServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="block text-xs text-gray-700 py-1 pl-2"
                  >
                    {service.name}
                  </Link>
                ))}

                <div className="text-xs font-bold text-[#168356] uppercase mt-2">
                  Patent
                </div>
                {patentServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="block text-xs text-gray-700 py-1 pl-2"
                  >
                    {service.name}
                  </Link>
                ))}

                <div className="text-xs font-bold text-[#168356] uppercase mt-2">
                  Company Formation
                </div>
                {companyFormationServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="block text-xs text-gray-700 py-1 pl-2"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/contact"
              className="block text-sm font-medium text-gray-900 py-2"
            >
              Contact
            </Link>
            <div className="text-sm font-medium text-gray-900 py-2">
              +1 (888) 669-0240
            </div>
            <Button
              onClick={() => openModal("registration")}
              className="w-full bg-[#d29b00] text-white hover:bg-[#a87b00]"
            >
              Register Trademark
            </Button>
          </div>
        </div>
      )}

      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
        packageTitle="Basic"
        packageAmount="45"
      />
    </header>
  );
};

export default Header;
